import { useState, useMemo } from 'react';
import {
  songRequests,
  topSongs,
  statusLabels,
  statusColors,
  type SongRequestStatus,
  type SongRequest,
} from '@/mocks/adminSongRequests';

type FilterStatus = SongRequestStatus | 'todas';

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('es-CR', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' });
}

function timeAgo(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diffMin = Math.floor((now - then) / 60000);
  if (diffMin < 1) return 'Ahora';
  if (diffMin < 60) return `Hace ${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `Hace ${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  return `Hace ${diffDay}d`;
}

const filterTabs: { id: FilterStatus; label: string; icon: string }[] = [
  { id: 'todas', label: 'Todas', icon: 'ri-inbox-line' },
  { id: 'pendiente', label: 'Pendientes', icon: 'ri-time-line' },
  { id: 'aceptada', label: 'Aceptadas', icon: 'ri-check-line' },
  { id: 'atendida', label: 'Atendidas', icon: 'ri-broadcast-line' },
  { id: 'reproducida', label: 'Transmitidas', icon: 'ri-check-double-fill' },
  { id: 'rechazada', label: 'Rechazadas', icon: 'ri-close-circle-line' },
];

export default function AdminSongRequestsPage() {
  const [requests, setRequests] = useState<SongRequest[]>(songRequests);
  const [activeFilter, setActiveFilter] = useState<FilterStatus>('todas');
  const [searchQuery, setSearchQuery] = useState('');

  /* ─── Counts ─── */
  const counts = useMemo(() => {
    const c: Record<string, number> = { pendiente: 0, aceptada: 0, atendida: 0, reproducida: 0, rechazada: 0, cancelada: 0, expirada: 0 };
    requests.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [requests]);

  /* ─── Filtered list ─── */
  const filtered = useMemo(() => {
    let list = requests;
    if (activeFilter !== 'todas') {
      list = list.filter((r) => r.status === activeFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (r) =>
          r.songName.toLowerCase().includes(q) ||
          r.artistName.toLowerCase().includes(q) ||
          r.requesterName.toLowerCase().includes(q) ||
          r.requesterLocation.toLowerCase().includes(q) ||
          (r.greeting && r.greeting.toLowerCase().includes(q)),
      );
    }
    return list;
  }, [requests, activeFilter, searchQuery]);

  /* ─── Actions ─── */
  const handleAccept = (item: SongRequest) => {
    setRequests((prev) =>
      prev.map((r) =>
        r.id === item.id
          ? { ...r, status: 'aceptada', acceptedAt: new Date().toISOString() }
          : r,
      ),
    );
  };

  const handleReject = (item: SongRequest) => {
    setRequests((prev) =>
      prev.map((r) =>
        r.id === item.id
          ? { ...r, status: 'rechazada', rejectedAt: new Date().toISOString(), rejectionReason: 'Rechazada por el operador.' }
          : r,
      ),
    );
  };

  // ⚠️ Marca ATENDIDA, NO PLAYED. Solo el backend confirma la transmisión real.
  const handleMarkAttended = (item: SongRequest) => {
    setRequests((prev) =>
      prev.map((r) =>
        r.id === item.id
          ? { ...r, status: 'atendida', attendedAt: new Date().toISOString(), operatorResponse: '¡Gracias por tu solicitud! El locutor ya la atendió al aire.' }
          : r,
      ),
    );
  };

  const totalRequests = requests.length;
  const pendingCount = counts.pendiente || 0;

  return (
    <div className="min-h-screen bg-background-50">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* ─── Header ─── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-accent-500 flex items-center justify-center">
                <i className="ri-music-2-fill text-background-50 text-lg"></i>
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-foreground-950">Solicitudes de Canciones y Saludos</h1>
                <p className="text-xs text-foreground-400 mt-0.5">Gestiona las peticiones que envían tus oyentes</p>
              </div>
            </div>
          </div>
          {/* Quick stats */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-background-100">
              <span className="text-2xl font-bold text-foreground-950">{totalRequests}</span>
              <span className="text-xs text-foreground-400">Total</span>
            </div>
            {pendingCount > 0 && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span>
                </span>
                <span className="text-2xl font-bold text-amber-600">{pendingCount}</span>
                <span className="text-xs text-amber-500">Pendientes</span>
              </div>
            )}
          </div>
        </div>

        {/* ─── Top Canciones Solicitadas ─── */}
        <div className="bg-background-50 border border-background-200 rounded-2xl p-5 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-lg bg-accent-100 flex items-center justify-center">
              <i className="ri-fire-fill text-accent-600 text-xs"></i>
            </div>
            <h2 className="text-sm font-bold text-foreground-900">Top Canciones Solicitadas</h2>
            <span className="text-[11px] text-foreground-400 ml-1">este mes</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {topSongs.map((song, idx) => (
              <div
                key={`${song.songName}-${song.artistName}`}
                className="flex items-start gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-accent-200 transition-colors cursor-pointer group"
              >
                <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-accent-700">{idx + 1}</span>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground-900 truncate group-hover:text-accent-700 transition-colors">
                    {song.songName}
                  </p>
                  <p className="text-xs text-foreground-500 truncate">{song.artistName}</p>
                  <p className="text-[10px] text-foreground-400 mt-0.5">
                    {song.requestCount} solicitudes
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ─── Filter bar ─── */}
        <div className="flex flex-wrap items-center gap-2 mb-5">
          {filterTabs.map((tab) => {
            const isActive = activeFilter === tab.id;
            const tabCount = tab.id === 'todas' ? totalRequests : (counts[tab.id] || 0);
            return (
              <button
                key={tab.id}
                onClick={() => setActiveFilter(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-all ${
                  isActive
                    ? 'bg-accent-500 text-background-50 shadow-sm'
                    : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                }`}
              >
                <i className={`${tab.icon} text-[11px]`}></i>
                {tab.label}
                <span className={`px-1.5 py-0 rounded-full text-[10px] font-bold ${
                  isActive ? 'bg-background-50/25 text-background-50' : 'bg-background-200 text-foreground-400'
                }`}>{tabCount}</span>
              </button>
            );
          })}
          {/* Search */}
          <div className="ml-auto">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
              <input
                type="text"
                placeholder="Buscar canción, artista u oyente..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-64 pl-8 pr-3 py-2 rounded-full bg-background-100 border border-background-200 text-xs text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-accent-300 transition-colors"
              />
            </div>
          </div>
        </div>

        {/* ─── Song Request Cards ─── */}
        <div className="space-y-3">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mb-3">
                <i className="ri-music-line text-foreground-300 text-2xl"></i>
              </div>
              <p className="text-sm text-foreground-500 font-medium mb-1">No hay solicitudes</p>
              <p className="text-xs text-foreground-400">
                {activeFilter === 'todas'
                  ? 'Aún no has recibido solicitudes de canciones'
                  : `No tienes solicitudes en estado "${statusLabels[activeFilter as SongRequestStatus]}"`}
              </p>
            </div>
          ) : (
            filtered.map((req) => {
              const sc = statusColors[req.status];
              const isRejected = req.status === 'rechazada';
              const isPlayed = req.status === 'reproducida';
              const isAttended = req.status === 'atendida';
              const isGreetingOnly = !req.songName && !!req.greeting;
              return (
                <div
                  key={req.id}
                  className={`rounded-xl border p-4 transition-all ${
                    isRejected
                      ? 'bg-background-50 border-background-200 opacity-75'
                      : isPlayed
                        ? 'bg-emerald-50/30 border-emerald-200'
                        : isAttended
                          ? 'bg-sky-50/30 border-sky-200'
                          : req.status === 'pendiente'
                            ? 'bg-amber-50/30 border-amber-200'
                            : req.status === 'aceptada'
                              ? 'bg-primary-50/30 border-primary-200'
                              : 'bg-background-50 border-background-200 hover:border-background-300'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className="w-12 h-12 rounded-xl bg-background-100 border border-background-200 flex items-center justify-center shrink-0">
                      <i className={`${isGreetingOnly ? 'ri-user-heart-line' : 'ri-music-fill'} text-foreground-400 text-xl`}></i>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {/* First row: song + status + time */}
                      <div className="flex items-center flex-wrap gap-2 mb-1.5">
                        {isGreetingOnly ? (
                          <span className="text-sm font-bold text-foreground-900">Solo saludo</span>
                        ) : (
                          <>
                            <span className="text-sm font-bold text-foreground-900">{req.songName}</span>
                            <span className="text-xs text-foreground-500">&middot;</span>
                            <span className="text-xs text-foreground-500">{req.artistName}</span>
                          </>
                        )}
                        {/* Status badge */}
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${sc.bg} ${sc.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`}></span>
                          {statusLabels[req.status]}
                        </span>
                        <span className="text-[11px] text-foreground-400 ml-auto">{timeAgo(req.requestedAt)}</span>
                      </div>

                      {/* Second row: requester */}
                      <div className="flex items-center gap-2.5 mb-3">
                        <div className="w-6 h-6 rounded-full bg-background-200 overflow-hidden shrink-0">
                          <img
                            src={req.requesterAvatar}
                            alt={req.requesterName}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                        <span className="text-xs font-semibold text-foreground-700">{req.requesterName}</span>
                        <span className="text-[11px] text-foreground-400 flex items-center gap-1">
                          <i className="ri-map-pin-line text-[10px]"></i>
                          {req.requesterLocation}
                        </span>
                        <span className="text-[10px] text-foreground-400 ml-auto">
                          {formatDate(req.requestedAt)}
                        </span>
                      </div>

                      {/* Greeting */}
                      {req.greeting && (
                        <div className="mb-3 p-2.5 rounded-lg bg-background-50 border border-background-200/60">
                          <p className="text-xs text-foreground-600 leading-relaxed">
                            &ldquo;{req.greeting}&rdquo;
                          </p>
                        </div>
                      )}

                      {/* Dedication */}
                      {req.dedication && (
                        <div className="mb-3 p-2.5 rounded-lg bg-rose-50 border border-rose-200/60">
                          <p className="text-xs text-foreground-600 italic leading-relaxed">
                            &ldquo;{req.dedication}&rdquo;
                          </p>
                        </div>
                      )}

                      {/* Rejection reason */}
                      {req.status === 'rechazada' && req.rejectionReason && (
                        <div className="mb-3 p-2.5 rounded-lg bg-rose-100/50 border border-rose-200/60">
                          <div className="flex items-start gap-1.5">
                            <i className="ri-information-line text-rose-600 text-xs mt-0.5 shrink-0"></i>
                            <p className="text-xs text-rose-700 leading-relaxed">{req.rejectionReason}</p>
                          </div>
                        </div>
                      )}

                      {/* Operator response */}
                      {req.operatorResponse && (
                        <div className="mb-3 p-2.5 rounded-lg bg-sky-50 border border-sky-200/60">
                          <div className="flex items-start gap-1.5">
                            <i className="ri-message-2-fill text-sky-600 text-xs mt-0.5 shrink-0"></i>
                            <p className="text-xs text-sky-700 leading-relaxed">{req.operatorResponse}</p>
                          </div>
                        </div>
                      )}

                      {/* Timeline metadata */}
                      <div className="flex items-center flex-wrap gap-3 mb-3">
                        {req.acceptedAt && (
                          <span className="text-[10px] text-foreground-400 flex items-center gap-1">
                            <i className="ri-check-line text-primary-500 text-[10px]"></i>
                            Aceptada: {formatTime(req.acceptedAt)}
                          </span>
                        )}
                        {req.attendedAt && (
                          <span className="text-[10px] text-sky-600 flex items-center gap-1 font-medium">
                            <i className="ri-broadcast-line text-[10px]"></i>
                            Atendida: {formatTime(req.attendedAt)}
                          </span>
                        )}
                        {req.playedAt && (
                          <span className="text-[10px] text-emerald-600 flex items-center gap-1 font-medium">
                            <i className="ri-check-double-fill text-[10px]"></i>
                            Transmitida: {formatTime(req.playedAt)}
                          </span>
                        )}
                        {req.rejectedAt && (
                          <span className="text-[10px] text-foreground-400 flex items-center gap-1">
                            <i className="ri-close-circle-fill text-rose-500 text-[10px]"></i>
                            Rechazada: {formatTime(req.rejectedAt)}
                          </span>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center flex-wrap gap-1.5">
                        {req.status === 'pendiente' && (
                          <>
                            <button
                              onClick={() => handleAccept(req)}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-500 text-background-50 text-[11px] font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
                            >
                              <i className="ri-check-line text-[11px]"></i>
                              Aceptar
                            </button>
                            <button
                              onClick={() => handleReject(req)}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-background-100 text-foreground-500 text-[11px] font-semibold hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
                            >
                              <i className="ri-close-line text-[11px]"></i>
                              Rechazar
                            </button>
                          </>
                        )}
                        {req.status === 'aceptada' && (
                          <>
                            <button
                              onClick={() => handleMarkAttended(req)}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-sky-500 text-background-50 text-[11px] font-semibold hover:bg-sky-600 cursor-pointer transition-colors whitespace-nowrap"
                            >
                              <i className="ri-broadcast-line text-[11px]"></i>
                              Marcar Atendida
                            </button>
                            <button
                              onClick={() => handleReject(req)}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-background-100 text-foreground-500 text-[11px] font-semibold hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
                            >
                              <i className="ri-close-line text-[11px]"></i>
                              Rechazar
                            </button>
                          </>
                        )}
                        {req.status === 'atendida' && (
                          <span className="inline-flex items-center gap-1 text-[11px] text-sky-700 font-medium px-3 py-1.5">
                            <i className="ri-broadcast-line text-[11px]"></i>
                            Atendida — esperando confirmación de transmisión
                          </span>
                        )}
                        {req.status === 'reproducida' && (
                          <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 font-medium px-3 py-1.5">
                            <i className="ri-check-double-line text-[11px]"></i>
                            Completada
                          </span>
                        )}
                        {req.status === 'rechazada' && (
                          <span className="inline-flex items-center gap-1 text-[11px] text-foreground-400 px-3 py-1.5">
                            <i className="ri-information-line text-[11px]"></i>
                            Sin acciones disponibles
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}